import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import {
  ChannelId,
  ChannelStatus,
  Device,
  DisplayMode,
  OS
} from '../../../../../domain/model/livestream/ILiveStreamRepository';
import { localStorage } from '../../../../../shared/localstorage';
import { ILiveStates } from '../../live/store';
import { ITopStates } from '../../top/store';
import { YjLivePlayerStreamCCU } from '@/public/template/common/store/live/livePlayer';

const actionTypes = {
  CHANGE_CHANNEL: 'liveStream/CHANGE_CHANNEL',
  REFLECT_LOCALSTORAGE: 'liveStream/REFLECT_LOCALSTORAGE',
  CHANGE_VOLUME: 'liveStream/CHANGE_VOLUME',
  CHANGE_MUTED: 'liveStream/CHANGE_MUTED',
  FETCH_WATCHING_USER_COUNT: 'liveStream/FETCH_WATCHING_USER_COUNT'
} as const;

const changeChannel = (channel: ChannelId) => {
  return {
    type: actionTypes.CHANGE_CHANNEL,
    payload: {
      channel
    }
  };
};

const reflectLocalStorage = {
  type: actionTypes.REFLECT_LOCALSTORAGE
};

const changeVolume = (volume: number) => {
  return {
    type: actionTypes.CHANGE_VOLUME,
    payload: {
      volume
    }
  };
};

const changeMuted = (muted: boolean) => {
  return {
    type: actionTypes.CHANGE_MUTED,
    payload: {
      muted
    }
  };
};

const updateWatchingUserCount = (payload: YjLivePlayerStreamCCU) => {
  return {
    type: actionTypes.FETCH_WATCHING_USER_COUNT,
    payload
  };
};

export type Action =
  | ReturnType<typeof changeChannel | typeof changeVolume | typeof changeMuted | typeof updateWatchingUserCount>
  | typeof reflectLocalStorage;

const fetchWatchingUserCount: ActionCreator<
  ThunkAction<Promise<void>, ILiveStates | ITopStates, void, Action>
> = () => {
  return async (dispatch: ThunkDispatch<ILiveStates | ITopStates, void, Action>) => {
    // memo: 変更する場合はstories配下のモック箇所もあわせて修正する
    const url = 'https://yjlive-player.yahooapis.jp/count/streamccu.json';

    return fetch(url, {
      method: 'GET'
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        const statusCode = res.status;
        return Promise.reject({ statusCode });
      })
      .then((body: YjLivePlayerStreamCCU) => {
        dispatch(updateWatchingUserCount(body));
      })
      .catch(err => {
        // do nothing
      });
  };
};

export const actions = {
  changeChannel,
  reflectLocalStorage,
  changeVolume,
  changeMuted,
  fetchWatchingUserCount
} as const;

interface IChannelData {
  id: ChannelId;
  status: ChannelStatus;
  name: string;
  plist: string;
  poster: string;
  device?: Device;
  countryCode: string;
  os?: OS;
  channelInfo?: {
    previousId: ChannelId;
    nextId: ChannelId;
    slideNumber: number;
  };
}
export interface ILiveStreamState {
  readonly title: string;
  readonly displayMode: DisplayMode;
  readonly currentChannel?: ChannelId;
  readonly defaultChannel: ChannelId;
  readonly requestChannel?: ChannelId;
  readonly channels: {
    nnn: IChannelData;
    jnn: IChannelData;
  };
  readonly volume: number;
  readonly muted: boolean;
  readonly isError: boolean;
  readonly isTablet: boolean;
  // 同じチャンネルを選択したときに、再読み込みするために必要なパラメータ
  readonly date: string;
  // 動画同時視聴者数
  readonly watchingUserCount: number;
}
const initialState: ILiveStreamState = {
  isError: true,
  isTablet: false,
  title: '',
  displayMode: DisplayMode.ON,
  defaultChannel: ChannelId.NNN,
  channels: {
    nnn: {
      id: ChannelId.NNN,
      status: ChannelStatus.TROUBLE,
      name: '',
      plist: '',
      poster: '',
      countryCode: ''
    },
    jnn: {
      id: ChannelId.JNN,
      status: ChannelStatus.TROUBLE,
      name: '',
      plist: '',
      poster: '',
      countryCode: ''
    }
  },
  date: '',
  volume: 0.0,
  muted: true,
  watchingUserCount: 0
};

export default (state: ILiveStreamState = initialState, action: Action): ILiveStreamState => {
  const LOCALSTORAGE_KEY = 'YAHOO.JP.News.Top.liveStreamCh';
  const LOCALSTORAGE_VER = 2;
  switch (action.type) {
    case actionTypes.CHANGE_CHANNEL: {
      const date = Date.now().toString();
      localStorage.set(LOCALSTORAGE_KEY, LOCALSTORAGE_VER, action.payload.channel);
      return {
        ...state,
        date,
        currentChannel: action.payload.channel
      };
    }
    case actionTypes.REFLECT_LOCALSTORAGE: {
      const channel: ChannelId | undefined = localStorage.get(LOCALSTORAGE_KEY, LOCALSTORAGE_VER);
      if (!channel) {
        return {
          ...state,
          currentChannel: state.defaultChannel
        };
      }
      return { ...state, currentChannel: channel };
    }
    case actionTypes.CHANGE_VOLUME:
      return { ...state, volume: action.payload.volume };
    case actionTypes.CHANGE_MUTED:
      return { ...state, muted: action.payload.muted };
    case actionTypes.FETCH_WATCHING_USER_COUNT: {
      // 日テレ、TBSの視聴者数を合計する
      // 日テレ:5743160747001, TBS:1684921345781868525
      // それぞれの視聴者数は必ず存在するので、undefinedチェックは不要
      const totalCount =
        (action.payload.playlist['5743160747001'] ?? 0) + (action.payload.playlist['1684921345781868525'] ?? 0);

      if (totalCount !== undefined && totalCount > 0) {
        return { ...state, watchingUserCount: totalCount };
      }
      return { ...state };
    }
    default:
      return state;
  }
};
